import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Upload } from 'lucide-react';
import { Select } from './form';
import { Button, cn } from './ui';
import { ApiError, api, csrfToken } from '../lib/api';

interface MediaFile {
  id: string;
  name: string;
  kind: string;
  asteriskBasename: string;
}

/**
 * Choose a prompt, or upload one without leaving the form.
 *
 * The upload is inline because the alternative is what we had: an admin building an IVR
 * discovers they need a greeting, navigates away, uploads it, and comes back to a form that has
 * lost everything they typed.
 */
export function PromptPicker({
  value,
  onChange,
  label,
  hint,
  required = false,
}: {
  value: string | null;
  onChange: (mediaId: string | null) => void;
  label: string;
  hint?: string | undefined;
  required?: boolean;
}) {
  const queryClient = useQueryClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: prompts = [] } = useQuery({
    queryKey: ['media'],
    queryFn: () => api.get<MediaFile[]>('/api/v1/media'),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('name', file.name.replace(/\.[^.]+$/, ''));
      body.append('kind', 'greeting');
      body.append('file', file);

      const token = csrfToken();
      const response = await fetch('/api/v1/media', {
        method: 'POST',
        credentials: 'same-origin',
        ...(token ? { headers: { 'x-csrf-token': token } } : {}),
        body,
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { error?: string };
        throw new ApiError(response.status, payload.error ?? 'upload failed', payload);
      }

      return (await response.json()) as { data: MediaFile };
    },
    onSuccess: (result) => {
      setError(null);
      // Selected immediately: uploading a prompt from inside a form means you want to use it.
      onChange(result.data.id);
      void queryClient.invalidateQueries({ queryKey: ['media'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'upload failed'),
  });

  const play = () => {
    const element = audioRef.current;
    if (!element || !value) return;
    element.src = `/api/v1/media/${value}/audio`;
    void element.play().catch(() => undefined);
  };

  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium">
        {required && <span className="mr-1 text-destructive">*</span>}
        {label}
      </span>

      <audio ref={audioRef} className="hidden" />

      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          <Select value={value ?? ''} onChange={(v) => onChange(v || null)}>
            <option value="">[None]</option>
            {prompts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>

        <button
          type="button"
          onClick={play}
          disabled={!value}
          className={cn(
            'shrink-0 rounded-[--radius] border border-border p-2 text-muted-foreground',
            value ? 'hover:bg-muted hover:text-foreground' : 'opacity-40',
          )}
          aria-label={`Play ${label}`}
          title="Play"
        >
          <Play size={15} />
        </button>

        <label
          className="shrink-0 cursor-pointer rounded-[--radius] border border-border p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Upload a new prompt"
        >
          <Upload size={15} />
          <input
            type="file"
            accept="audio/*,.wav,.mp3,.m4a,.ogg"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setUploading(true);
              upload.mutate(file, { onSettled: () => setUploading(false) });
              e.target.value = '';
            }}
          />
        </label>
      </div>

      {uploading && <span className="block text-xs text-muted-foreground">Uploading…</span>}
      {error && <span className="block text-xs text-destructive">{error}</span>}
      {!error && hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

/** Kept beside the picker so a caller can offer the same upload affordance elsewhere. */
export function UploadPromptButton({ onUploaded }: { onUploaded: (id: string) => void }) {
  return (
    <Button
      type="button"
      variant="secondary"
      onClick={() => onUploaded('')}
      className="hidden"
      aria-hidden
    >
      Upload
    </Button>
  );
}
